import React, { useEffect, useMemo, useRef, useState } from 'react';
import './App.css';
import { AnimatePresence, motion } from 'framer-motion';
import { gsap } from 'gsap';

import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInAnonymously,
  signInWithPopup,
  signOut
} from 'firebase/auth';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';

import { auth, db } from './firebase';
import { enrollFingerprint, fingerprintAuthenticate, hasEnrolledFingerprint } from './fingerprint';

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

const EMPTY_FORM = {
  username: '',
  password: '',
  category: 'General',
  url: ''
};

function App() {
  const [fingerprintReady, setFingerprintReady] = useState(hasEnrolledFingerprint());
  const [unlocked, setUnlocked] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const [passwords, setPasswords] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [editingId, setEditingId] = useState(null);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const orbOneRef = useRef(null);
  const orbTwoRef = useRef(null);

  const ensureUserSession = async () => {
    if (auth.currentUser) {
      return auth.currentUser;
    }

    try {
      const credential = await signInAnonymously(auth);
      return credential.user;
    } catch (err) {
      if (err.code === 'auth/admin-restricted-operation') {
        throw new Error(
          'No active Firebase session. Use Continue with Google, or enable Anonymous sign-in in Firebase Console > Authentication > Sign-in method.'
        );
      }
      throw err;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!orbOneRef.current || !orbTwoRef.current) {
      return undefined;
    }

    const tweenOne = gsap.to(orbOneRef.current, {
      y: -25,
      x: 12,
      rotate: 8,
      duration: 5,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut'
    });

    const tweenTwo = gsap.to(orbTwoRef.current, {
      y: 20,
      x: -14,
      rotate: -6,
      duration: 6,
      repeat: -1,
      yoyo: true,
      ease: 'sine.inOut'
    });

    return () => {
      tweenOne.kill();
      tweenTwo.kill();
    };
  }, []);

  useEffect(() => {
    if (!unlocked) {
      return undefined;
    }

    let unsubscribe = () => {};

    const connect = async () => {
      try {
        const user = await ensureUserSession();
        const userId = user.uid;
        const passwordsRef = collection(db, 'users', userId, 'passwords');
        const q = query(passwordsRef, orderBy('createdAt', 'desc'));

        unsubscribe = onSnapshot(q, (snapshot) => {
          const docs = snapshot.docs.map((snap) => ({
            id: snap.id,
            ...snap.data()
          }));
          setPasswords(docs);
        });
      } catch (err) {
        setError(`Firebase connection failed: ${err.message}`);
      }
    };

    connect();

    return () => unsubscribe();
  }, [unlocked]);

  const categories = useMemo(() => {
    const dynamic = Array.from(new Set(passwords.map((item) => item.category || 'General'))).sort();
    return ['All', ...dynamic];
  }, [passwords]);

  const filteredPasswords = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return passwords.filter((item) => {
      const matchesCategory =
        categoryFilter === 'All' || (item.category || 'General') === categoryFilter;

      const matchesSearch =
        !keyword ||
        [item.username, item.category, item.url]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(keyword));

      return matchesCategory && matchesSearch;
    });
  }, [categoryFilter, passwords, search]);

  const vaultStats = useMemo(() => {
    const weakPasswords = passwords.filter((item) => (item.password || '').length < 10).length;
    const websiteEntries = passwords.filter((item) => Boolean(item.url)).length;

    return {
      total: passwords.length,
      weak: weakPasswords,
      websites: websiteEntries
    };
  }, [passwords]);

  const userId = auth.currentUser?.uid;

  const handleEnrollFingerprint = async () => {
    setError(null);
    setStatus('');
    setLoading(true);
    try {
      await enrollFingerprint();
      setFingerprintReady(true);
      setStatus('Fingerprint enrolled successfully.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFingerprintUnlock = async () => {
    setError('');
    setStatus('');
    setLoading(true);
    try {
      await fingerprintAuthenticate();
      await ensureUserSession();
      setUnlocked(true);
      setStatus('Unlocked with fingerprint.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setStatus('');
    setLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
      setUnlocked(true);
      setStatus('Signed in with Google.');
    } catch (err) {
      setError(`Google login failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGuestLogin = async () => {
    setError('');
    setStatus('');
    setLoading(true);
    try {
      await signInAnonymously(auth);
      setUnlocked(true);
      setStatus('Continuing as guest.');
    } catch (err) {
      if (err.code === 'auth/admin-restricted-operation') {
        setError(
          'Guest login is disabled in Firebase. Enable Anonymous sign-in in Firebase Console > Authentication > Sign-in method.'
        );
        return;
      }
      setError(`Guest login failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    setError('');
    setStatus('');
    setLoading(true);
    try {
      await signOut(auth);
      setUnlocked(false);
      setPasswords([]);
      setStatus('Signed out.');
    } catch (err) {
      setError(`Sign out failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
  };

  const handleSavePassword = async (event) => {
    event.preventDefault();

    if (!form.username.trim() || !form.password.trim()) {
      setError('Username and password are required.');
      return;
    }

    setError('');
    setStatus('');
    setLoading(true);

    try {
      const activeUser = auth.currentUser || (await ensureUserSession());
      const activeUserId = activeUser.uid;

      const payload = {
        ...form,
        username: form.username.trim(),
        category: form.category.trim() || 'General',
        url: form.url.trim(),
        updatedAt: serverTimestamp()
      };

      if (editingId) {
        const ref = doc(db, 'users', activeUserId, 'passwords', editingId);
        await updateDoc(ref, payload);
        setStatus('Password updated.');
      } else {
        const ref = collection(db, 'users', activeUserId, 'passwords');
        await addDoc(ref, {
          ...payload,
          createdAt: serverTimestamp()
        });
        setStatus('Password saved.');
      }

      resetForm();
    } catch (err) {
      setError(`Save failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      username: item.username || '',
      password: item.password || '',
      category: item.category || 'General',
      url: item.url || ''
    });
  };

  const handleDelete = async (id) => {
    if (!userId) {
      return;
    }

    try {
      const ref = doc(db, 'users', userId, 'passwords', id);
      await deleteDoc(ref);
      setStatus('Password deleted.');
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const togglePasswordVisibility = (id) => {
    setVisiblePasswords((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  if (!unlocked) {
    return (
      <div className="app-shell">
        <div className="ambient-layer">
          <div ref={orbOneRef} className="orb orb-one" />
          <div ref={orbTwoRef} className="orb orb-two" />
        </div>

        <main className="lock-layout">
          <motion.section
            className="card intro-panel"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="eyebrow">VaultKey Security Suite</p>
            <h1 className="hero-title">A smarter vault for every login you own.</h1>
            <p className="hero-copy">
              Unlock with biometrics, Google, or guest mode. Your passwords stay synced per account and accessible across laptops.
            </p>
            <div className="hero-tags">
              <span>Biometric Ready</span>
              <span>Google Sync</span>
              <span>PWA Friendly</span>
            </div>
          </motion.section>

          <motion.section
            className="card auth-panel"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h2>Welcome back</h2>
            <p className="subtitle">Choose your preferred sign-in method.</p>

            <div className="auth-actions">
              <button className="btn btn-primary" onClick={handleEnrollFingerprint} disabled={loading}>
                {loading ? 'Working...' : 'Enroll Fingerprint'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleFingerprintUnlock}
                disabled={loading || !fingerprintReady}
              >
                {loading ? 'Working...' : 'Unlock with Fingerprint'}
              </button>
              <button className="btn btn-secondary" onClick={handleGoogleLogin} disabled={loading}>
                {loading ? 'Working...' : 'Continue with Google'}
              </button>
              <button className="btn btn-secondary" onClick={handleGuestLogin} disabled={loading}>
                {loading ? 'Working...' : 'Continue as Guest'}
              </button>
            </div>

            {!fingerprintReady && (
              <p className="hint">Enroll once on this device, then unlock using biometrics only.</p>
            )}

            {status && <p className="status">{status}</p>}
            {error && <p className="error">{error}</p>}
          </motion.section>
        </main>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <div className="ambient-layer">
        <div ref={orbOneRef} className="orb orb-one" />
        <div ref={orbTwoRef} className="orb orb-two" />
      </div>

      <main className="manager-layout">
        <motion.header
          className="card manager-hero"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div>
            <p className="eyebrow">Secure Dashboard</p>
            <h1 className="title">VaultKey</h1>
            <p className="subtitle">Keep credentials organized, searchable, and secure.</p>
            {currentUser?.email && <p className="subtitle">Signed in as {currentUser.email}</p>}
          </div>

          <div className="actions hero-actions">
            <button
              className="btn btn-secondary"
              onClick={() => {
                setUnlocked(false);
                setStatus('Locked.');
              }}
            >
              Lock
            </button>
            {!currentUser?.isAnonymous && currentUser && (
              <button className="btn btn-danger" onClick={handleSignOut} disabled={loading}>
                Sign Out
              </button>
            )}
          </div>
        </motion.header>

        <section className="stats-grid">
          <motion.article
            className="stat-card"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <p>Total Entries</p>
            <h3>{vaultStats.total}</h3>
          </motion.article>
          <motion.article
            className="stat-card"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
          >
            <p>Weak Passwords</p>
            <h3>{vaultStats.weak}</h3>
          </motion.article>
          <motion.article
            className="stat-card"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <p>Entries with URL</p>
            <h3>{vaultStats.websites}</h3>
          </motion.article>
        </section>

        <section className="manager-grid">
          <motion.section
            className="card surface form-panel"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45 }}
          >
            <h2>{editingId ? 'Edit Credential' : 'Add New Credential'}</h2>
            <p className="subtitle">Quickly save your account details in one place.</p>

            <form className="form-grid" onSubmit={handleSavePassword}>
              <input
                className="input"
                name="username"
                placeholder="Username / Email"
                value={form.username}
                onChange={handleInputChange}
              />
              <input
                className="input"
                name="password"
                type="password"
                placeholder="Password"
                value={form.password}
                onChange={handleInputChange}
              />
              <input
                className="input"
                name="category"
                placeholder="Category (Work, Personal, Banking...)"
                value={form.category}
                onChange={handleInputChange}
              />
              <input
                className="input"
                name="url"
                placeholder="Website URL"
                value={form.url}
                onChange={handleInputChange}
              />

              <div className="actions form-actions">
                <button className="btn btn-primary" type="submit" disabled={loading}>
                  {loading ? 'Saving...' : editingId ? 'Update Password' : 'Save Password'}
                </button>
                {editingId && (
                  <button className="btn btn-secondary" type="button" onClick={resetForm}>
                    Cancel Edit
                  </button>
                )}
              </div>
            </form>

            {status && <p className="status">{status}</p>}
            {error && <p className="error">{error}</p>}
          </motion.section>

          <motion.section
            className="card surface vault-panel"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45, delay: 0.05 }}
          >
            <div className="toolbar">
              <input
                className="input"
                placeholder="Search username, category, site..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <select
                className="input select"
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <section className="list">
              <AnimatePresence mode="popLayout">
                {filteredPasswords.length === 0 ? (
                  <motion.p
                    className="hint"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    No passwords found for the current search/category.
                  </motion.p>
                ) : (
                  filteredPasswords.map((item, index) => (
                    <motion.article
                      className="entry"
                      key={item.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -12 }}
                      transition={{ delay: index * 0.03 }}
                    >
                      <div>
                        <h3>{item.username}</h3>
                        <p>
                          Password: {visiblePasswords[item.id] ? item.password : '••••••••'}
                        </p>
                        <p>Category: {item.category || 'General'}</p>
                        {item.url && <p>URL: {item.url}</p>}
                      </div>
                      <div className="actions column-actions">
                        <button className="btn btn-secondary" onClick={() => togglePasswordVisibility(item.id)}>
                          {visiblePasswords[item.id] ? 'Hide' : 'Show'}
                        </button>
                        <button className="btn btn-secondary" onClick={() => handleEdit(item)}>
                          Edit
                        </button>
                        <button className="btn btn-danger" onClick={() => handleDelete(item.id)}>
                          Delete
                        </button>
                      </div>
                    </motion.article>
                  ))
                )}
              </AnimatePresence>
            </section>
          </motion.section>
        </section>
      </main>
    </div>
  );
}

export default App;
